import { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { motion, AnimatePresence } from "framer-motion";
import bgImage from "@/asset/images/fourth-bg.png";
import bgImageTablet from "@/asset/images/tablet/fourth-bg.png";
import bgImageMobile from "@/asset/images/mobile/bg-fourth.png";
import advertisingServiceImg from "@/asset/images/service/advertising.png";
import analysisServiceImg from "@/asset/images/service/analysis.png";
import communityServiceImg from "@/asset/images/service/community.png";
import eventsServiceImg from "@/asset/images/service/events.png";
import giveawayServiceImg from "@/asset/images/service/giveaway.png";
import mediaServiceImg from "@/asset/images/service/media.png";
import newsServiceImg from "@/asset/images/service/news.png";
import videoServiceImg from "@/asset/images/service/video.png";
import ArrowIcon from "@/asset/icons/arrow-icon.svg";
import bgLeft from "@/asset/images/service/bg-left.svg";
import bgRight from "@/asset/images/service/bg-right.svg";
import bgRightTablet from "@/asset/images/tablet/service-bg.png";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRouter } from "next/navigation";
export default function ServicesDetailPage() {
  const router = useRouter();
  const [activeService, setActiveService] = useState("news-blogs");

  const [windowWidth, setWindowWidth] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swipeDirection, setSwipeDirection] = useState<"left" | "right" | null>(
    null
  );
  const sliderRef = useRef<HTMLDivElement>(null);

  const isTablet = windowWidth < 1280;
  const isMobile = windowWidth < 768;

  const services: {
    id: string;
    title: string;
    url: string;
    details: {
      title: string;
      items: string[];
      icon: string;
    };
  }[] = [
    {
      id: "news-blogs",
      title: "News & Blogs",
      url: "/details?heading=Content%20&%20Insights",
      details: {
        title: "News & Blogs",
        items: [
          "Short Market News",
          "In-depth Insights",
          "SEO - Optimized Blogs",
          "Thematic Blog Series",
        ],
        icon: newsServiceImg.src,
      },
    },
    {
      id: "analysis-reports",
      title: "In-Depth Analysis & Reports",
      url: "/details?heading=Research%20&%20Reports",
      details: {
        title: "In-Depth Analysis & Reports",
        items: [
          "Market Reports",
          "Project & Brand PR",
          "Educational Series",
          '"Hidden Gem" & High-Potential Projects',
          "Market Event Analysis",
        ],
        icon: analysisServiceImg.src,
      },
    },
    {
      id: "social-media",
      title: "Social Media",
      url: "/details?heading=Social%20Media%20&%20Community%20Management",
      details: {
        title: "Social Media",
        items: ["Content Creation", "Paid Promotion"],
        icon: mediaServiceImg.src,
      },
    },
    {
      id: "community-management",
      title: "Community Management",
      url: "/details?heading=Social%20Media%20&%20Community%20Management",
      details: {
        title: "Community Management",
        items: ["Community Moderator", "Campaign Management for Social"],
        icon: communityServiceImg.src,
      },
    },
    {
      id: "advertising",
      title: "Advertising & Sponsored Content",
      url: "/details?heading=Advertising%20&%20Sponsored%20Content",
      details: {
        title: "Advertising & Sponsored Content",
        items: ["Sponsored Articles", "Advertising Banners"],
        icon: advertisingServiceImg.src,
      },
    },
    {
      id: "video-podcast",
      title: "Video & Podcast Production",
      url: "/details?heading=Video%20&%20Podcast%20Production%20(on%20demand)",
      details: {
        title: "Video & Podcast Production",
        items: [
          "Brand Video Production",
          "Branded Placement in Klarda's Video Content",
          "Industry-Leading Podcast",
        ],
        icon: videoServiceImg.src,
      },
    },
    {
      id: "events",
      title: "Online & Offline Events",
      url: "/details?heading=Events%20&%20Promotions",
      details: {
        title: "Online & Offline Events",
        items: [
          "AMA Hosting",
          "Educational & Tutorial Webinars",
          "Offline events",
        ],
        icon: eventsServiceImg.src,
      },
    },
    {
      id: "merchandise",
      title: "Merchandise & Event Giveaways",
      url: "/details?heading=Merchandise%20&%20Giveaways",
      details: {
        title: "Merchandise & Event Giveaways",
        items: [
          "Merchandise Production",
          "Advanced Merchandise Design Package",
        ],
        icon: giveawayServiceImg.src,
      },
    },
  ];

  // Find the active service
  const activeServiceData =
    services.find((s) => s.id === activeService) || services[0];
  const activeServiceIndex = services.findIndex((s) => s.id === activeService);

  // Check window size on component mount and resize
  useEffect(() => {
    const checkScreenSize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Set initial value
    checkScreenSize();

    // Add event listener
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Original handler function - preserved exactly
  const handleOpenDetail = (title: string) => {
    const service = services.find((s) => s.details.title === title);
    if (!service) return;
    const url = service.url;
    router.push(url);
  };

  // Mobile slider functions
  const handleServiceSelection = (id: string) => {
    if (isAnimating) return;
    setActiveService(id);
  };

  const goToNext = () => {
    if (isAnimating) return;

    const nextIndex = (activeServiceIndex + 1) % services.length;
    setSwipeDirection("right");
    setIsAnimating(true);
    setActiveService(services[nextIndex].id);
  };

  const goToPrev = () => {
    if (isAnimating) return;

    const prevIndex =
      activeServiceIndex === 0 ? services.length - 1 : activeServiceIndex - 1;
    setSwipeDirection("left");
    setIsAnimating(true);
    setActiveService(services[prevIndex].id);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 50) {
      // Swipe left (next)
      goToNext();
    }

    if (touchStart - touchEnd < -50) {
      // Swipe right (prev)
      goToPrev();
    }
  };

  // Animation variants for mobile slider
  const slideVariants = {
    hiddenLeft: {
      x: "-100%",
      opacity: 0,
    },
    hiddenRight: {
      x: "100%",
      opacity: 0,
    },
    visible: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
    exitLeft: {
      x: "-100%",
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
    exitRight: {
      x: "100%",
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeInOut",
      },
    },
  };

  // Reset animation flag when animation completes
  const handleAnimationComplete = () => {
    setIsAnimating(false);
  };

  return (
    <section
      id='services'
      className='flex flex-col justify-start items-center gap-6 lg:gap-[33px] py-8 bg-cover bg-bottom bg-no-repeat'
      style={{
        backgroundImage: isMobile
          ? `url(${bgImageMobile.src})`
          : isTablet
          ? `url(${bgImageTablet.src})`
          : `url(${bgImage.src})`,
      }}>
      {/* Hero Section - Unchanged */}
      <motion.div
        className='mx-auto'
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}>
        <div className='text-center'>
          <motion.h1
            className='text-[24px] md:text-[40px] lg:text-[68px] font-bold mb-2 lg:mb-6 leading-tight lg:leading-[88px]'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}>
            Klarda Media Services
          </motion.h1>
          <motion.p
            className='text-base md:text-lg lg:text-2xl leading-normal lg:leading-[38px] mx-auto'
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}>
            We offer a full range of marketing and branding services tailored
            for crypto projects
          </motion.p>
        </div>
      </motion.div>

      {/* Services Content - Desktop View */}
      {!isTablet && (
        <div className='w-full flex flex-col lg:flex-row gap-2 lg:gap-[22px] md:w-[1124px] mb-10'>
          {/* Service Menu - Original desktop code preserved exactly */}
          <div
            className='w-full lg:w-[470px] shadow-card p-3 sm:p-4 md:p-5 bg-cover md:bg-contain bg-right bg-no-repeat rounded-[25px]'
            style={{
              backgroundImage: `url(${bgLeft.src})`,
              backgroundSize: "cover",
              backgroundPosition: "bottom",
            }}>
            <div className='bg-white h-full rounded-[25px] overflow-y-auto max-h-[400px] lg:max-h-none'>
              <motion.ul
                variants={{
                  hidden: { opacity: 0 },
                  visible: {
                    opacity: 1,
                    transition: {
                      staggerChildren: 0.1,
                      delayChildren: 0.2,
                    },
                  },
                }}
                initial='hidden'
                animate='visible'>
                {services.map((service) => (
                  <motion.li
                    key={service.id}
                    variants={{
                      hidden: { y: 20, opacity: 0 },
                      visible: {
                        y: 0,
                        opacity: 1,
                        transition: { duration: 0.5, ease: "easeOut" },
                      },
                    }}
                    whileTap={{ scale: 0.98 }}
                    className={`h-[50px] sm:h-[60px] pl-3 sm:pl-[25px] flex items-center cursor-pointer transition-colors duration-200 hover:rounded-[18px] text-base sm:text-lg md:text-xl lg:text-2xl leading-tight lg:leading-8 ${
                      activeService === service.id
                        ? "rounded-[18px] text-white bg-btn-linear font-bold"
                        : "text-[#0D0978] hover:bg-blue-50"
                    }`}
                    onClick={() => setActiveService(service.id)}>
                    {service.title}
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>

          {/* Service Details - Original desktop code preserved exactly */}
          <div
            className='w-full flex-1 lg:w-[620px] shadow-card p-3 sm:p-4 md:p-5 relative bg-contain bg-right bg-no-repeat'
            style={{
              backgroundImage: `url(${bgRight.src})`,
            }}>
            <AnimatePresence mode='wait'>
              {activeServiceData && (
                <div className='absolute right-4 md:right-8 top-4 sm:top-2 z-[1000] w-8 h-8 sm:w-10 sm:h-10 md:w-[45px] md:h-[45px]'>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}>
                    <Image
                      src={ArrowIcon.src}
                      alt='icon'
                      width={45}
                      height={45}
                      className='cursor-pointer w-full h-full'
                      onClick={() =>
                        handleOpenDetail(activeServiceData.details.title)
                      }
                    />
                  </motion.div>
                </div>
              )}
            </AnimatePresence>

            <div className='h-full min-h-[350px] lg:min-h-[500px] rounded-[25px]'>
              <AnimatePresence mode='wait'>
                {activeServiceData && (
                  <div
                    key={activeService}
                    className='flex flex-col p-4 md:p-5 justify-end h-full pl-6 md:pl-[40px] bg-no-repeat bg-top bg-contain min-h-[350px] lg:min-h-[500px] relative'
                    style={{
                      backgroundImage: `url(${activeServiceData.details.icon})`,
                    }}>
                    {/* Animated background image (left to right) */}
                    <motion.div
                      className='absolute inset-0 bg-no-repeat bg-top bg-contain z-0'
                      style={{
                        backgroundImage: `url(${activeServiceData.details.icon})`,
                      }}
                      key={`image-${activeService}`}
                      initial='hidden'
                      animate='visible'
                      exit={{
                        opacity: 0,
                        x: 100,
                        transition: { duration: 0.3 },
                      }}
                      variants={{
                        hidden: { x: 0, opacity: 0 },
                        visible: {
                          x: 0,
                          opacity: 1,
                          transition: { duration: 0.6, ease: "easeOut" },
                        },
                      }}
                    />

                    {/* Content container (bottom to top) */}
                    <motion.div
                      className='z-10 mt-auto'
                      key={`content-${activeService}`}
                      initial='hidden'
                      animate='visible'
                      exit={{
                        opacity: 0,
                        y: -30,
                        transition: { duration: 0.3 },
                      }}
                      variants={{
                        hidden: { y: 50, opacity: 0 },
                        visible: {
                          y: 0,
                          opacity: 1,
                          transition: { duration: 0.6, ease: "easeOut" },
                        },
                      }}>
                      <motion.h2 className='text-xl sm:text-2xl md:text-3xl font-semibold text-accent mb-3 md:mb-4 break-words sm:text-nowrap'>
                        {activeServiceData.details.title}
                      </motion.h2>

                      <ul className='space-y-1 mb-4 md:mb-[10px]'>
                        {activeServiceData.details.items.map((item, idx) => (
                          <li key={idx} className='flex items-start'>
                            <span className='mr-2 mt-1'>•</span>
                            <span className='text-base md:text-2xl'>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  </div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}

      {/* Mobile/Tablet Slider View */}
      {isTablet && (
        <div className='w-full px-4 pb-6'>
          <div
            className='relative'
            ref={sliderRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}>
            {/* Slider Navigation Buttons */}
            <div className='hidden md:block absolute top-1/2 -translate-y-1/2 left-5 z-20'>
              <button
                onClick={goToPrev}
                disabled={isAnimating}
                className='w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow-md'
                aria-label='Previous service'>
                <ChevronLeft size={20} className='text-accent' />
              </button>
            </div>

            <div className='hidden md:block absolute top-1/2 -translate-y-1/2 right-5 z-20'>
              <button
                onClick={goToNext}
                disabled={isAnimating}
                className='w-8 h-8 rounded-full bg-white/90 flex items-center justify-center shadow-md'
                aria-label='Next service'>
                <ChevronRight size={20} className='text-accent' />
              </button>
            </div>

            {/* Service Card Slider */}
            <div className='overflow-hidden'>
              <AnimatePresence initial={false} mode='wait'>
                <motion.div
                  key={activeService}
                  variants={slideVariants}
                  initial={
                    swipeDirection === "left" ? "hiddenLeft" : "hiddenRight"
                  }
                  animate='visible'
                  exit={swipeDirection === "left" ? "exitRight" : "exitLeft"}
                  onAnimationComplete={handleAnimationComplete}
                  className='rounded-[30px] overflow-hidden flex justify-center items-center'>
                  {/* Service Card Design */}
                  <div
                    className='bg-white md:bg-transparent border-[2px] border-[#9747FF] md:border-none relative rounded-[30px] overflow-hidden p-6 min-h-[400px] md:min-h-[620px] w-[545px] flex flex-col justify-end bg-no-repeat bg-center bg-contain'
                    style={{
                      backgroundImage: isMobile
                        ? "none"
                        : `url(${bgRightTablet.src})`,
                    }}>
                    {/* Service Content */}
                    <div
                      className='flex-1 w-full h-full bg-no-repeat bg-top bg-contain flex flex-col justify-end px-4 py-0 md:p-10'
                      style={{
                        backgroundImage: `url(${activeServiceData.details.icon})`,
                      }}>
                      {/* Arrow Button (Top Right) */}

                      {/* Service Title */}
                      <h3 className='text-[#443DFF] text-2xl sm:text-3xl font-bold mb-4 flex gap-4 items-center justify-between md:justify-start'>
                        <span>
                          {activeServiceData.details.title}
                        </span>
                        <div className='min-w-[30px]'>
                          <motion.div
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}>
                            <Image
                              src={ArrowIcon.src}
                              alt='Learn more'
                              width={30}
                              height={30}
                              className='cursor-pointer'
                              onClick={() =>
                                handleOpenDetail(
                                  activeServiceData.details.title
                                )
                              }
                            />
                          </motion.div>
                        </div>
                      </h3>

                      {/* Service Items List */}
                      <ul className='space-y-0 md:space-y-2 text-left pl-2'>
                        {activeServiceData.details.items.map((item, idx) => (
                          <li key={idx} className='flex items-start'>
                            <span className='text-[#443DFF] font-bold mr-2'>
                              •
                            </span>
                            <span className='text-[#333] font-medium'>
                              {item}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Pagination Dots */}
          <div className='flex justify-center mt-4 space-x-2'>
            {services.map((service, index) => (
              <button
                key={service.id}
                onClick={() => handleServiceSelection(service.id)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  activeService === service.id
                    ? "w-4 bg-[#443DFF]"
                    : "bg-gray-300"
                }`}
                aria-label={`View ${service.title}`}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
